import { useState, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { TwinClient } from "../lib/client/client.js";

export function useTwinPersona(twinUrl) {
  let info = useQuery({
    queryKey: ["GET", twinUrl, "/info"],
    queryFn: async function getInfoQueryFn() {
      if (!twinUrl) return null;
      return await TwinClient.info(twinUrl);
    },

    retry: (n, err) => {
      if (err?.data?.status == 404) {
        return false;
      }
      return n < 3
    },

    enabled: !!twinUrl,
  });

  let persona = useQuery({
    queryKey: ["GET", twinUrl, "/persona"],
    queryFn: async function getPersonaFn() {
      if (!twinUrl) return null;
      let res = await TwinClient.persona(twinUrl);
      let info = res?.instances[0]?.info || res?.root?.info;
      try {
        return JSON.parse(info);
      } catch (_) {
        return info;
      }
    },

    retry: (n, err) => {
      if (err?.data?.status == 404) {
        return false;
      }
      return n < 3
    },

    enabled: !!twinUrl && !!info.data,
  });

  return { info, persona }
}


function Pill({ twinUrl, persona = {} }) {

  function personaText(p) {
    if (p.name) {
      if (p.email) {
        return `${p.name} (${p.email})`
      } else {
        return p.email
      }
    } else if (p.email) {
      return p.email;
    } else {
      return null;
    }
  }

  if (!persona.name && !persona.email) {
    return <></>;
  } else {
    return <div className="px-1 flex flex-row items-center w-full py-1 rounded-full border border-gray-400">
      <div className="flex rounded-full size-10 aspect aspect-square text-xl font-semibold bg-gray-400 text-white items-center justify-center">
        <p className="overflow-hidden overflow-ellipsis select-none"> {personaText(persona).slice(0, 2).toUpperCase()} </p>
      </div>
      <div className="w-full flex flex-col ml-2 rounded-r-full overflow-hidden">
        <div className="w-full">{personaText(persona)}</div>
        <div className="w-full text-[0.70rem] text-gray-400 text-nowrap overflow-hidden overflow-ellipsis">{twinUrl}</div>
      </div>
    </div>;
  }
}

function validateTwinUrl(twinUrl) {
  try {

    let [schema, rest] = twinUrl.split("https://");
    if ((schema == "" && rest === undefined) || schema.length != 0) {
      return { error: "Must start with https://" };
    }

    let [addr] = rest.split(".");

    if (addr.length != 32) {
      return { error: "Address must be 32 characters long" };
    }
    let hexMatch = addr.match(/^[0-9a-f]+$/i)?.[0];
    if (hexMatch != addr) {
      return { error: "Address must string must be hex" };
    }

    return null;
  } catch (e) {
    console.warn(e)
    return { error: "error", data: e };
  }
}


function EditSVG({ fill = "000000", className = "" }) {
  return <svg className={className} fill={fill} viewBox="0 0 24 24" >
    <path d="M6.5,24H0v-6.5L17.5,0L24,6.5L6.5,24z M2,22h3.7L18,9.7L14.3,6L2,18.3V22z M15.7,4.6l3.7,3.7l1.8-1.8l-3.7-3.7L15.7,4.6z" />
  </svg>
}

export default function TwinUrlInput({ name, required, placeholder }) {

  function handleChange(e) {
    // validates input value and sets state
    // .error if validation fails
    // .value if validation successful
    let input = e.target;
    if (!input.value || input.value == "") {
      input.setCustomValidity("");
      setTwinUrl({}); // let `required` prop manage empty values!
    } else {
      let { error, data } = validateTwinUrl(input.value) || {};
      if (error) {
        input.setCustomValidity(error);
        console.debug("error", error)
        setTwinUrl({ error })
        if (data) {
          console.warn(data);
        }
      } else {
        input.setCustomValidity("");
        setTwinUrl({ value: input.value });
      }
    }
  }

  const [twinUrl, setTwinUrl] = useState({});
  const { info, persona } = useTwinPersona(twinUrl.value);

  const [isEditing, setIsEditing] = useState(true);
  const isInputVisible = isEditing || !persona.data;

  const inputRef = useRef(0);

  useEffect(_ => {
    if (isEditing) { // triggers on false -> true transition
      inputRef.current.focus();
    }
  }, [isEditing]);

  let inputStatus = {};

  if (twinUrl.error) {
    inputStatus = { error: twinUrl.error };
  } else if (!twinUrl.value) {
    inputStatus = {};
  } else if (info.isPending) {
    inputStatus = { info: "Attempting to reach twin..." };
  } else if (info.isError) {
    inputStatus = { error: "Twin address not reachable!" };
  } else if (info.data) {
    if (persona.isPending) {
      inputStatus = { success: "Twin address reachable; getting persona info" };
    } else if (!persona.isPending && !persona.data) {
      inputStatus = { success: "Twin address reachable (no persona info available)" };
    } else if (persona.data) {
      inputStatus = { success: `Twin address reachable (${persona.data.name})` };
    } else {
      inputStatus = {};
    }
  } else {
    inputStatus = {};
  }

  useEffect(_ => {
    let { error, warn, info, success } = inputStatus;
    if (success) {
      inputRef.current.setCustomValidity("");
    } else {
      inputRef.current.setCustomValidity(error || warn || info);
    }
  }, [inputStatus, inputRef]);

  function StatusBar({ status }) {
    let { error, warn, info, success } = status;
    if (error) {
      return <p className="text-xs text-error">{error}</p>;
    }
    if (warn) {
      return <p className="text-xs text-warn">{warn}</p>;
    }
    if (info) {
      return <p className="text-xs text-info">{info}</p>;
    }
    if (success) {
      return <p className="text-xs text-success">{success}</p>;
    }
    return <></>;
  }

  return <div>
    <div style={{ display: isInputVisible ? "flex" : "none" }} className="flex-col gap-1">
      <input
        ref={inputRef}
        name={name}
        required={required}
        type="text"
        onChange={handleChange}
        onBlur={_ => setIsEditing(false)}
        data-error={!!inputStatus.error}
        placeholder={placeholder}
        defaultValue={twinUrl.value || ""}
        className="w-full px-2 py-1 border rounded
          data-[error=true]:focus:outline
          data-[error=true]:focus:outline-1
          data-[error=true]:focus:outline-error
          data-[error=true]:border-error
          data-[error=true]:shadow-[0px_0px_3px_#ef2626]" />
      <StatusBar status={inputStatus} />
    </div>
    <div style={{ display: isInputVisible ? "none" : "flex" }} className="flex-row gap-1">
      <Pill twinUrl={twinUrl.value} persona={persona.data} />
      <button type="button" onClick={_ => setIsEditing(true)}
        className="border border-gray-400 rounded-full size-12 p-3 aspect-square items-center hover:bg-gray-100" >
        <EditSVG fill="#4b5563" className="hover:fill-black" />
      </button>
    </div>
  </div>;
}
